import { Box } from '../primitives/Box.js';
import { Pressable } from '../primitives/Pressable.js';
import { Text } from '../primitives/Text.js';
import { cx } from '../primitives/cx.js';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';

export type TabItem = { id: string; icon: IconName; label: string };

/** The centre button. Not a destination — the one thing you *do* from anywhere. */
export type TabAction = { icon?: IconName; label: string; onSelect: () => void };

export type TabBarProps = {
  items: readonly TabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  /** Rendered between the halves of `items` on a phone, above them on desktop. */
  action?: TabAction;
  /** `vertical` is the desktop rail: the same slab, stood on its end. */
  orientation?: 'horizontal' | 'vertical';
  className?: string;
};

/**
 * Four destinations and one action, deliberately quiet — the list is the screen.
 *
 * A floating slab rather than an edge-to-edge bar: rounded a little, bordered
 * on all four sides, raised off the canvas. It owns its own shape; where it
 * floats is the shell's decision, which is what lets the same component sit
 * under a thumb on a phone and halfway up the left of a desktop.
 *
 * The action is the largest thing here on purpose. Whatever is biggest on
 * screen is what the app is understood to be for, so this is a *doing* button,
 * never a generic "+ Add" — accumulating is not the point of the product.
 */
export function TabBar({
  items,
  activeId,
  onSelect,
  action,
  orientation = 'horizontal',
  className,
}: TabBarProps) {
  const vertical = orientation === 'vertical';

  const tab = (item: TabItem) => {
    const active = item.id === activeId;
    return (
      <Pressable
        key={item.id}
        aria-current={active ? 'page' : undefined}
        onClick={() => onSelect(item.id)}
        className={cx(
          'flex flex-col items-center justify-center gap-0 rounded-lg py-1',
          vertical ? 'px-2' : 'flex-1',
          active ? 'text-accent' : 'text-subtle',
          // Colour alone is not a state. The tint says it a second way.
          active && 'bg-accent-soft',
        )}
      >
        <Icon name={item.icon} size={19} />
        <Text
          variant="caption"
          className={cx('text-current', active ? 'font-semibold' : 'font-medium')}
        >
          {item.label}
        </Text>
      </Pressable>
    );
  };

  // Split around the middle so the action sits between the halves. An odd
  // count leans the extra tab left, which keeps the button on centre.
  const middle = Math.floor(items.length / 2);
  const before = vertical ? items : items.slice(0, middle);
  const after = vertical ? [] : items.slice(middle);

  const button = action && (
    <Pressable
      data-coach="now-action"
      aria-label={action.label}
      aria-haspopup="dialog"
      onClick={action.onSelect}
      className={cx(
        'grid place-items-center rounded-full bg-accent text-on-accent shadow-md',
        'hover:bg-accent-hi',
        // Raised clear of the slab on a phone: the thumb target and the
        // hierarchy are the same gesture.
        vertical ? 'size-12 shrink-0 self-center' : '-mt-4 size-13 shrink-0 self-center',
      )}
    >
      <Icon name={action.icon ?? 'bolt'} size={24} />
    </Pressable>
  );

  return (
    <Box
      as="nav"
      className={cx(
        // Translucent, so the list keeps going underneath rather than
        // stopping at an opaque slab. Blur only where it is supported; the
        // solid background stays as the floor.
        'flex rounded-xl border border-line bg-raised shadow-md',
        'supports-[backdrop-filter]:bg-raised/85 supports-[backdrop-filter]:backdrop-blur-md',
        vertical
          ? 'flex-col items-stretch gap-1 p-2'
          : 'items-center justify-around gap-1 px-1.5 py-1',
        className,
      )}
    >
      {vertical && button}
      {vertical && button && <Box className="mx-1 h-px bg-line" />}
      {before.map(tab)}
      {!vertical && button}
      {after.map(tab)}
    </Box>
  );
}
